import { Box, Typography } from '@mui/material'
import { useMemo } from 'react'
import { formatToK, transformPieChartData } from '../utils/transformPieChartData'
import { DataPieChart, PieChartDataItem } from 'pages/dataSentry/components/DataPieChart'
import { ChartCard } from '../components/ChartCard'
import { useDataSentryContext } from '../context/DataSentryContext'
import { useDataSentryEventsByClassification } from 'api/dataSentry/queries/useDataSentryEventsByClassification'
import { ClassificationType } from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { AuthorizationStatus as SystemsAuthorizationStatus } from '@ketch-com/windlass/dist/dataSentry/v1/systems/systems'
import { Card, Icon, theme, IconDictionary, Spinner } from '@ketch-com/deck'
import pluralize from 'pluralize'
import { OverAllSpectrum } from 'pages/dataSentry/overview/OverAllSpectrum'
import { AuthorizationStatus as TrackersAuthorizationStatus } from '@ketch-com/windlass/dist/dataSentry/v1/trackers/trackers'
import { TrackersTable } from 'pages/dataSentry/overview/TrackersTable'
import { SystemsTable } from 'pages/dataSentry/overview/SystemsTable'
import { ConsentControl } from 'pages/dataSentry/overview/ConsentControl'
import { DataTypesCollected } from 'pages/dataSentry/overview/DataTypesCollected'
import { transformTrackerPurposePieChart } from 'pages/dataSentry/utils/transformTrackerPurposePieChart'
import { transformTrackerCategoriesData } from 'pages/dataSentry/utils/transformTrackersCategoriesData'
import { useDataSentryPieChartTrackersPurpose } from 'api/dataSentry/queries/useDataSentryPieChartTrackersPurpose'
import { useDataSentryTrackersByCategory } from 'api/dataSentry/queries/useDataSentryPieChartTrackers'
import { TopDataCollectingSystemItem } from 'pages/dataSentry/systems/systemsTabComponents/TopDataCollectingSystemItem'

export interface GenericPieChartProps {
  data: PieChartDataItem[]
  isLoading: boolean
}

export const GenericPieChart: React.FC<GenericPieChartProps> = ({ data, isLoading }) => {
  const chartData = isLoading || !data || data.length === 0 ? [] : data
  return <DataPieChart data={chartData} />
}

export const OverviewTab = () => {
  const {
    selectedTimeRange,
    systemsSummary,
    selectedPropertyCode,
    eventsSummary,
    isEventsSummaryLoading,
    trackersSummary,
    topSystems,
    isTopSystemsLoading,
    topUnauthorizedSystems,
    isTopUnauthorizedSystemsLoading,
    dataTypesTotalCount,
    hasPropertyAndScanTimeRange,
  } = useDataSentryContext()

  const { data: categoryTrackerPieData, isLoading: isTrackerCategoryDataLoading } = useDataSentryTrackersByCategory({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      ...(selectedPropertyCode && {
        'property-codes': selectedPropertyCode,
      }),
    },
    enabled: hasPropertyAndScanTimeRange,
  })
  const { data: purposeTrackerPieData, isLoading: isTrackerPurposeDataLoading } = useDataSentryPieChartTrackersPurpose({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      ...(selectedPropertyCode && {
        'property-codes': selectedPropertyCode,
      }),
    },
    enabled: hasPropertyAndScanTimeRange,
  })

  const { data: categoryData, isLoading: isCategoryDataLoading } = useDataSentryEventsByClassification({
    params: {
      'classification-type': ClassificationType.DATA_CATEGORY,
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
    },
    enabled: hasPropertyAndScanTimeRange,
  })

  const spectrumData = useMemo(() => {
    if (!eventsSummary?.classificationTypes?.length) {
      return { categoryCount: 0, totalCategoryCount: 0 }
    }
    const dataCategoryObj = eventsSummary.classificationTypes.find(item => item.classificationType === 'DATA_CATEGORY')

    if (dataCategoryObj) {
      return {
        categoryCount: dataCategoryObj.classificationCount || 0,
        totalCategoryCount: dataCategoryObj.totalClassificationCount || 0,
      }
    }
  }, [eventsSummary?.classificationTypes])

  const unauthorizedSystems = systemsSummary?.authorizationTypes?.find(
    system => system.status === SystemsAuthorizationStatus.UNAUTHORIZED,
  )
  const unauthorizedSystemsCount = unauthorizedSystems?.count || 0

  const unauthorizedTrackers = trackersSummary?.authorizationTypes?.find(
    tracker => tracker.status === TrackersAuthorizationStatus.UNAUTHORIZED,
  )

  const unauthorizedTrackersCount = unauthorizedTrackers?.count || 0

  const transformedDataCategoriesData = useMemo(
    () => transformPieChartData(categoryData?.classifications || []),
    [categoryData],
  )

  const transformedTrackerCategoriesData = useMemo(
    () => transformTrackerCategoriesData(categoryTrackerPieData),
    [categoryTrackerPieData],
  )

  const transformedTrackerPurposeData = useMemo(() => {
    if (!purposeTrackerPieData) return []
    return transformTrackerPurposePieChart(purposeTrackerPieData)
  }, [purposeTrackerPieData])

  const topDataCollectingSystem = topSystems?.[0] || {}
  const topUnauthorizedSystem = topUnauthorizedSystems?.[0] || {}

  return (
    <Box width="100%" display="flex" flexDirection="column" gap={6} pb={16} mt={3}>
      <Box display="flex" gap={3}>
        <OverAllSpectrum
          maxCategoryCount={spectrumData?.totalCategoryCount}
          categoryCount={spectrumData?.categoryCount || 0}
          isLoading={isEventsSummaryLoading}
        />
        <Box display="flex" flexWrap="wrap" flexBasis={402} gap={2}>
          <Box flexBasis={193} display="flex" flexDirection="column">
            <Card
              cardVariant="stretchWidth"
              cardTitle={formatToK(trackersSummary?.total || 0)}
              subTitle={pluralize('Tracker', trackersSummary?.total)}
              primaryIcon={
                <Icon name={IconDictionary.Trackers} width="32px" height="32px" iconColor={theme.palette.Black.o48} />
              }
              isNumerical
              backgroundColor={trackersSummary?.total ? theme.palette.White.o100 : 'transparent'}
              textColor={trackersSummary?.total ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
              sx={{
                border: trackersSummary?.total ? 'none' : `1px solid ${theme.palette.Black.o8}`,
                padding: 2,
              }}
            />
          </Box>
          <Box flexBasis={193} display="flex" flexDirection="column">
            <Card
              cardVariant="stretchWidth"
              cardTitle={formatToK(systemsSummary?.total || 0)}
              subTitle={pluralize('System', systemsSummary?.total)}
              primaryIcon={
                <Icon name={IconDictionary.System} width="32px" height="32px" iconColor={theme.palette.Black.o48} />
              }
              isNumerical
              backgroundColor={systemsSummary?.total ? theme.palette.White.o100 : 'transparent'}
              textColor={systemsSummary?.total ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
              sx={{
                border: systemsSummary?.total ? 'none' : `1px solid ${theme.palette.Black.o8}`,
                padding: 2,
              }}
            />
          </Box>

          <Box flexBasis={193} display="flex" flexDirection="column">
            <Card
              cardVariant="stretchWidth"
              cardTitle={formatToK(unauthorizedTrackersCount || 0)}
              subTitle={pluralize('Unauthorized Tracker', unauthorizedTrackersCount)}
              primaryIcon={
                <Icon
                  name={IconDictionary.OCookieUnknown}
                  width="32px"
                  height="32px"
                  iconColor={theme.palette.Black.o48}
                />
              }
              isNumerical
              backgroundColor={unauthorizedTrackersCount ? theme.palette.White.o100 : 'transparent'}
              textColor={unauthorizedTrackersCount ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
              sx={{
                border: unauthorizedTrackersCount ? 'none' : `1px solid ${theme.palette.Black.o8}`,
                padding: 2,
              }}
            />
          </Box>

          <Box flexBasis={193} display="flex" flexDirection="column">
            <Card
              cardVariant="stretchWidth"
              cardTitle={formatToK(unauthorizedSystemsCount || 0)}
              subTitle={pluralize('Unauthorized System', unauthorizedSystemsCount)}
              primaryIcon={
                <Icon
                  name={IconDictionary.OSystemUnknown}
                  width="32px"
                  height="32px"
                  iconColor={theme.palette.Black.o48}
                />
              }
              isNumerical
              backgroundColor={unauthorizedSystemsCount ? theme.palette.Black.o8 : 'transparent'}
              textColor={unauthorizedSystemsCount ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
              sx={{
                border: systemsSummary?.total ? 'none' : `1px solid ${theme.palette.Black.o8}`,
                padding: 2,
              }}
            />
          </Box>
        </Box>
        <ChartCard title="Data Categories Collected" isLoading={isCategoryDataLoading}>
          <GenericPieChart data={transformedDataCategoriesData} isLoading={isCategoryDataLoading} />
        </ChartCard>
      </Box>

      <Box display="flex" gap={3}>
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          gap={3}
          sx={{ backgroundColor: theme.palette.Common.Container }}
          borderRadius="11px"
          px={2.5}
          py={2}
        >
          <Typography variant="h4">Trackers</Typography>

          <Box
            display="flex"
            gap={2}
            borderRadius="11px"
            sx={{
              border: `1px solid ${theme.palette.Black.o8}`,
            }}
          >
            <Box flex={1} px={2} py={1.5} display="flex" flexDirection="column" gap={2}>
              <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
                Tracker Categories
              </Typography>

              {isTrackerCategoryDataLoading ? (
                <Box display="flex" alignItems="center" justifyContent="center" height={120}>
                  <Spinner size="32px" thickness={2} />
                </Box>
              ) : (
                <DataPieChart
                  data={transformedTrackerCategoriesData}
                  height={80}
                  innerRadius={20}
                  outerRadius={40}
                  responsiveContainerWidth={80}
                />
              )}
            </Box>
            <Box flex={1} px={2} py={1.5} display="flex" flexDirection="column" gap={2}>
              <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
                Tracker Purposes
              </Typography>

              {isTrackerPurposeDataLoading ? (
                <Box display="flex" alignItems="center" justifyContent="center" height={120}>
                  <Spinner size="32px" thickness={2} />
                </Box>
              ) : (
                <DataPieChart
                  data={transformedTrackerPurposeData}
                  height={80}
                  innerRadius={20}
                  outerRadius={40}
                  responsiveContainerWidth={80}
                />
              )}
            </Box>
          </Box>
          <TrackersTable />
        </Box>
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          gap={3}
          sx={{ backgroundColor: theme.palette.Common.Container }}
          borderRadius="11px"
          px={2.5}
          py={2}
        >
          <Typography variant="h4">Systems</Typography>
          <Box
            gap={2}
            borderRadius="11px"
            sx={{
              border: `1px solid ${theme.palette.Black.o8}`,
            }}
          >
            {isTopSystemsLoading || isTopUnauthorizedSystemsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={230}>
                <Spinner size="32px" thickness={2} />
              </Box>
            ) : (
              <Box display="flex" justifyContent="space-evenly" py={1} height={230}>
                <Box display="flex" gap={2} flexDirection="column" textAlign="center">
                  <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
                    Top Data Collector
                  </Typography>
                  <TopDataCollectingSystemItem
                    rank={1}
                    name={topDataCollectingSystem.name}
                    code={topDataCollectingSystem.code}
                    logo={topDataCollectingSystem.logo}
                    collectionEvents={topDataCollectingSystem.collectionEvents}
                    dataTypeCount={topDataCollectingSystem.dataTypeCount}
                    trackerCount={topDataCollectingSystem.trackerCount}
                    hasData={true}
                    totalDataTypeCount={dataTypesTotalCount}
                  />
                </Box>
                <Box display="flex" gap={2} flexDirection="column" textAlign="center">
                  <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
                    Top Unauthorized
                  </Typography>
                  <TopDataCollectingSystemItem
                    rank={1}
                    name={topUnauthorizedSystem.name}
                    code={topUnauthorizedSystem.code}
                    logo={topUnauthorizedSystem.logo}
                    collectionEvents={topUnauthorizedSystem.collectionEvents}
                    dataTypeCount={topUnauthorizedSystem.dataTypeCount}
                    trackerCount={topUnauthorizedSystem.trackerCount}
                    hasData={true}
                    totalDataTypeCount={dataTypesTotalCount}
                  />
                </Box>
              </Box>
            )}
          </Box>
          <SystemsTable />
        </Box>
      </Box>
      <Box display="flex" gap={3} minHeight={480}>
        <ConsentControl />
        <DataTypesCollected />
      </Box>
    </Box>
  )
}
